import { baseLocale, isLocale, type Locale } from '@/paraglide/runtime.js'
import { preferredSupportedLocale } from './accept-language'

export const GIPHY_LOCALE_HEADER = 'x-giphy-locale'

// Deliberately ours rather than Paraglide's `cookieName`. Paraglide's client
// runtime never reads this (strategy is ['baseLocale']), and Fastly's VCL reads
// this exact name before forwarding X-Giphy-Locale. A library upgrade that
// renames its internal cookie should not be able to break that edge contract.
export const GIPHY_LOCALE_COOKIE = 'giphy_locale'

export const GIPHY_LOCALE_COOKIE_OPTIONS = {
    path: '/',
    // Nothing client-side reads the locale, so keep it away from scripts.
    httpOnly: true,
    sameSite: 'lax',
    maxAge: 60 * 60 * 24 * 400,
} as const

type RequestLocaleInput = {
    headerLocale: string | null | undefined
    cookieLocale?: string | null | undefined
    acceptLanguage?: string | null | undefined
    forceBaseLocale?: boolean
    allowCookieFallback?: boolean
}

/**
 * The localization kill switch. Forces the base locale on every route,
 * whatever the header or cookie says. Read at call time rather than at module
 * load so the proxy helpers and env.ts share one definition and a test can
 * stub it without reloading modules.
 */
export const isBaseLocaleForced = () => process.env.GIPHY_FORCE_BASE_LOCALE === 'true'

export const resolveSupportedLocale = (
    locale: string | null | undefined,
    options: { forceBaseLocale?: boolean } = {}
): Locale => {
    if (options.forceBaseLocale) {
        return baseLocale
    }

    return isLocale(locale) ? locale : baseLocale
}

/**
 * Resolution order is header, then cookie, then Accept-Language.
 *
 * The cookie sits above Accept-Language deliberately: it is only ever written
 * by the language switcher, so it represents a choice someone made. Someone
 * with a Spanish phone who picks English must stay in English, on every later
 * request, or the switcher is decorative. Auto-detection is the answer only for
 * visitors who have not expressed a preference.
 */
export const resolveRequestLocale = ({
    headerLocale,
    cookieLocale,
    acceptLanguage,
    forceBaseLocale,
    allowCookieFallback,
}: RequestLocaleInput): Locale => {
    if (forceBaseLocale) {
        return baseLocale
    }

    if (isLocale(headerLocale)) {
        return headerLocale
    }

    if (allowCookieFallback && isLocale(cookieLocale)) {
        return cookieLocale
    }

    // Accept-Language is only safe on responses that are never shared-cached.
    // The proxy controls that by allowing this fallback only for no-store paths.
    if (allowCookieFallback) {
        const preferred = preferredSupportedLocale(acceptLanguage)

        if (preferred) {
            return preferred
        }
    }

    return baseLocale
}
